/**
 * MONSTER TOWN — ERRANDS. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * The connective tissue of "A Day in Town". The owner's sentence is the spec:
 *
 *   "oh no, the zoo animals are hungry and they're out of food — whip something
 *    up in the kitchen and bring it."
 *
 * One errand that starts at a neighbour, passes through the kid's own kitchen,
 * and ends across town. That is `errand_zoo_hungry` in `src/content/errands.ts`,
 * and this engine exists to make that shape expressible in DATA.
 *
 * ===========================================================================
 *  LAW #2 IS STRUCTURAL HERE. READ BEFORE ADDING A FIELD.
 * ===========================================================================
 *  - There is no `fail()`, no `abandon()`, no `expiresAt`, no `deadline`, no
 *    `timeLimit`, no `status: 'failed'`. An errand has exactly three states:
 *    offerable, running, done. It waits forever.
 *  - `ErrandRun.hidden` puts the CARD away, not the errand. Nothing is lost and
 *    it can be shown again with one tap.
 *  - Several errands run at once and in any order. Nothing is exclusive.
 *  - When a step is entered it is IMMEDIATELY re-checked against what the kid
 *    already has (`autoSatisfy`), so a kid who happened to pick the fruit first
 *    is never made to do it twice.
 *  - Steps carry `have`/`need` counts, so the card is a row of dots a kid can
 *    count. No wording is required anywhere (LAW #4).
 *
 * ===========================================================================
 *  HOW A CREW REPORTS PROGRESS
 * ===========================================================================
 * One wire, so no crew imports another and no crew has to know about errands:
 *
 *   bus.emit('errand:observe', { kind: 'gather', what: 'ing_apple', count: 3 })
 *
 * `build:placed`, `inventory:gained` and `collection:found` are ADAPTED here for
 * free, so the build/inventory/collection crews need do nothing at all.
 */

import './dayEvents'
import { bus } from './events'
import { clock } from './time'

// ---------------------------------------------------------------------------
// steps
// ---------------------------------------------------------------------------

export type ErrandStepKind = 'talk' | 'gather' | 'make' | 'deliver' | 'build' | 'catch'

interface StepCommon {
  /** Icon key for the 3-icon step strip. Pictures only (LAW #4). */
  readonly icon: string
  /** Where the guide arrow points: a scene id or a landmark key. Data. */
  readonly at?: string
  /** Optional voice key for "here is what to do". Supports the picture, never replaces it. */
  readonly voice?: string
}

/** Go and see somebody. */
export interface TalkStep extends StepCommon {
  readonly kind: 'talk'
  readonly npc: string
}
/** Get some things. Satisfied by the bag, so already having them counts. */
export interface GatherStep extends StepCommon {
  readonly kind: 'gather'
  readonly item: string
  readonly count: number
  /** Hint only — where they usually come from. Never a restriction. */
  readonly from?: string
}
/** Cook something. */
export interface MakeStep extends StepCommon {
  readonly kind: 'make'
  readonly recipe: string
  readonly count?: number
}
/** Carry something to somebody (or something — the zoo's feeder is a `to`). */
export interface DeliverStep extends StepCommon {
  readonly kind: 'deliver'
  readonly item: string
  readonly to: string
  readonly count?: number
}
/** Build something — the neighbour-request class of errand. */
export interface BuildStep extends StepCommon {
  readonly kind: 'build'
  /** Either a specific piece... */
  readonly defId?: string
  /** ...or any piece of a category ('plant', 'bed', 'fence'). */
  readonly category?: string
  readonly count: number
  /** Optional scene the pieces must land in. */
  readonly sceneId?: string
}
/** Catch a fish or a bug. */
export interface CatchStep extends StepCommon {
  readonly kind: 'catch'
  /** A specific species... */
  readonly species?: string
  /** ...or any of a kind ('fish' | 'bug'). */
  readonly group?: string
  readonly count: number
}

export type ErrandStep =
  | TalkStep
  | GatherStep
  | MakeStep
  | DeliverStep
  | BuildStep
  | CatchStep

/** How many of the thing this step wants. Always >= 1. */
export function stepNeed(s: ErrandStep): number {
  switch (s.kind) {
    case 'gather':
    case 'build':
    case 'catch':
      return Math.max(1, Math.round(s.count))
    case 'make':
    case 'deliver':
      return Math.max(1, Math.round(s.count ?? 1))
    case 'talk':
      return 1
  }
}

/** The content id this step is about — what an observation must match. */
export function stepSubject(s: ErrandStep): string {
  switch (s.kind) {
    case 'talk':
      return s.npc
    case 'gather':
      return s.item
    case 'make':
      return s.recipe
    case 'deliver':
      return s.item
    case 'build':
      return s.defId ?? s.category ?? ''
    case 'catch':
      return s.species ?? s.group ?? ''
  }
}

// ---------------------------------------------------------------------------
// definitions — DATA
// ---------------------------------------------------------------------------

export interface ErrandReward {
  readonly coins?: number
  /** itemId -> count handed to the bag. */
  readonly items?: Readonly<Record<string, number>>
  /** Placeable id granted (owned forever). */
  readonly grants?: readonly string[]
}

/** WHEN an errand shows up. Data; the offer system reads it, this file stores it. */
export interface ErrandOffer {
  /** How it arrives: a neighbour flags you down, a note in the mailbox, a venue. */
  readonly from: 'neighbour' | 'mailbox' | 'venue' | 'start'
  /** Only offered inside this hour window. Wraps. Undefined = any time. */
  readonly fromHour?: number
  readonly toHour?: number
  /** Not before this in-world day. */
  readonly afterDay?: number
  /** Every one of these must be finished first. */
  readonly requires?: readonly string[]
}

export interface ErrandDef {
  readonly id: string
  /** DEV ONLY. Never shown (LAW #4). */
  readonly devName: string
  /** The face on the card. An NPC id. */
  readonly giver: string
  /** Icon key for the card. */
  readonly icon: string
  /** NON-EMPTY BY TYPE — an errand with no steps is a compile error. */
  readonly steps: readonly [ErrandStep, ...ErrandStep[]]
  readonly reward?: ErrandReward
  readonly offer?: ErrandOffer
  /** Errand id granted the moment this one finishes. Chains. */
  readonly chain?: string
  /** 'daily' errands become offerable again on the next in-world day. */
  readonly repeat?: 'once' | 'daily'
}

/** Authoring helper — mirrors `definePlaceable`. Keeps the tuple type. */
export function defineErrand<D extends ErrandDef>(def: D): D {
  return def
}

const DEFS = new Map<string, ErrandDef>()

export function registerErrand(def: ErrandDef): ErrandDef {
  DEFS.set(def.id, def)
  return def
}
export function registerErrands(list: readonly ErrandDef[]): void {
  for (const d of list) registerErrand(d)
}
export function getErrand(id: string): ErrandDef | undefined {
  return DEFS.get(id)
}
export function allErrands(): ErrandDef[] {
  return [...DEFS.values()]
}

// ---------------------------------------------------------------------------
// runs
// ---------------------------------------------------------------------------

export interface ErrandRun {
  readonly id: string
  /** Index of the current step. Equals `steps.length` when finished. */
  step: number
  /** How much of the CURRENT step is done. */
  have: number
  /** Card put away by the kid. The errand itself is untouched. */
  hidden: boolean
  /** In-world day it was granted. */
  grantedDay: number
}

export interface ErrandCard {
  readonly id: string
  readonly def: ErrandDef
  readonly giver: string
  readonly icon: string
  readonly stepIndex: number
  readonly step: ErrandStep | null
  readonly have: number
  readonly need: number
  readonly hidden: boolean
  readonly done: boolean
  /** Icon per step, current one flagged — the 3-icon strip, verbatim. */
  readonly strip: ReadonlyArray<{ icon: string; done: boolean; current: boolean }>
  /** Where the guide arrow should point, if the step said. */
  readonly pointAt: string | null
}

export interface ErrandsSaveState {
  runs: Array<{ id: string; step: number; have: number; hidden: boolean; grantedDay: number }>
  done: Array<{ id: string; day: number }>
}

/** One thing that happened, which may advance a step. */
export interface Observation {
  readonly kind: ErrandStepKind
  /** The content id: an item, a recipe, an npc, a placeable id or category. */
  readonly what: string
  readonly count?: number
  /** For `deliver`: who/what it went to. For `build`: the scene it landed in. */
  readonly to?: string
  readonly sceneId?: string
  /** For `catch`: 'fish' | 'bug', so a `group` step matches. */
  readonly group?: string
  /** For `build`: the piece's category, so a `category` step matches. */
  readonly category?: string
}

/**
 * How the engine reads the world when a step is entered, so a kid who already
 * did the thing is never made to do it again. Injected rather than imported so
 * this file stays free of the bag and the book (and stays unit-testable).
 */
export interface ErrandWorldReader {
  /** How many of `itemId` are in the bag. */
  countItem: (itemId: string) => number
  /** How many of `species` (or of `group`) are in the collection book. */
  countFound: (idOrGroup: string) => number
}

class Errands {
  private runs = new Map<string, ErrandRun>()
  private done = new Map<string, number>()
  private reader: ErrandWorldReader = { countItem: () => 0, countFound: () => 0 }

  /** Wired once by `installErrands`. */
  setWorldReader(r: ErrandWorldReader): void {
    this.reader = r
  }

  // -- reading -------------------------------------------------------------

  run(id: string): ErrandRun | null {
    return this.runs.get(id) ?? null
  }
  active(): ErrandRun[] {
    return [...this.runs.values()]
  }
  isRunning(id: string): boolean {
    return this.runs.has(id)
  }
  isDone(id: string): boolean {
    return this.done.has(id)
  }
  completedIds(): string[] {
    return [...this.done.keys()].sort()
  }

  stepOf(id: string): ErrandStep | null {
    const r = this.runs.get(id)
    const def = r === undefined ? undefined : DEFS.get(id)
    if (r === undefined || def === undefined) return null
    return def.steps[r.step] ?? null
  }

  /** Everything the HUD needs to draw one card. Pictures only. */
  card(id: string): ErrandCard | null {
    const r = this.runs.get(id)
    const def = DEFS.get(id)
    if (r === undefined || def === undefined) return null
    const step = def.steps[r.step] ?? null
    return {
      id,
      def,
      giver: def.giver,
      icon: def.icon,
      stepIndex: r.step,
      step,
      have: r.have,
      need: step === null ? 0 : stepNeed(step),
      hidden: r.hidden,
      done: r.step >= def.steps.length,
      strip: def.steps.map((s, i) => ({
        icon: s.icon,
        done: i < r.step,
        current: i === r.step,
      })),
      pointAt: step?.at ?? null,
    }
  }

  cards(): ErrandCard[] {
    return this.active()
      .map((r) => this.card(r.id))
      .filter((c): c is ErrandCard => c !== null)
  }

  // -- offering ------------------------------------------------------------

  /**
   * Is this errand available to be offered right now? Pure read — the offer
   * system (crew-owned) decides HOW it arrives; this says whether it may.
   */
  isOfferable(id: string): boolean {
    const def = DEFS.get(id)
    if (def === undefined) return false
    if (this.runs.has(id)) return false
    if (this.done.has(id)) {
      if (def.repeat !== 'daily') return false
      if ((this.done.get(id) ?? 0) >= clock.day) return false
    }
    const o = def.offer
    if (o === undefined) return true
    if (o.afterDay !== undefined && clock.day < o.afterDay) return false
    if (o.requires !== undefined && !o.requires.every((r) => this.done.has(r))) return false
    if (o.fromHour !== undefined || o.toHour !== undefined) {
      const f = ((o.fromHour ?? 0) % 24 + 24) % 24
      const t = ((o.toHour ?? 24) % 24 + 24) % 24
      const h = clock.hour
      const inside = f === t ? true : f < t ? h >= f && h < t : h >= f || h < t
      if (!inside) return false
    }
    return true
  }

  offerable(): ErrandDef[] {
    return allErrands().filter((d) => this.isOfferable(d.id))
  }

  // -- granting ------------------------------------------------------------

  /**
   * The kid took the errand. Idempotent: granting a running errand returns the
   * run that is already there. Immediately auto-satisfies step 0 against what
   * the kid already has.
   */
  grant(id: string, opts: { silent?: boolean } = {}): ErrandRun | null {
    const def = DEFS.get(id)
    if (def === undefined) return null
    const existing = this.runs.get(id)
    if (existing !== undefined) return existing
    if (def.repeat !== 'daily') this.done.delete(id)
    const run: ErrandRun = { id, step: 0, have: 0, hidden: false, grantedDay: clock.day }
    this.runs.set(id, run)
    if (opts.silent !== true) bus.emit('errand:granted', { id, giver: def.giver })
    this.autoSatisfy(run, def)
    return run
  }

  /** Put the card away. The errand keeps waiting — nothing is lost (LAW #2). */
  setCardHidden(id: string, hidden: boolean): void {
    const r = this.runs.get(id)
    if (r === undefined) return
    r.hidden = hidden
    bus.emit('errand:cardHidden', { id, hidden })
  }

  // -- progress ------------------------------------------------------------

  /**
   * Something happened. Every RUNNING errand whose current step matches is
   * advanced. Returns how many steps completed across all errands.
   *
   * Deliberately no `false` return: an observation that matches nothing is a
   * perfectly normal thing for a kid to do, not an error.
   */
  observe(o: Observation): number {
    let advanced = 0
    for (const run of [...this.runs.values()]) {
      const def = DEFS.get(run.id)
      if (def === undefined) continue
      const step = def.steps[run.step]
      if (step === undefined) continue
      if (!matches(step, o)) continue
      run.have += Math.max(1, Math.round(o.count ?? 1))
      bus.emit('errand:progressed', {
        id: run.id,
        step: run.step,
        have: run.have,
        need: stepNeed(step),
      })
      if (run.have >= stepNeed(step)) advanced += this.completeStep(run, def)
    }
    return advanced
  }

  private completeStep(run: ErrandRun, def: ErrandDef): number {
    let completed = 0
    let guard = 0
    while (run.step < def.steps.length && guard++ < 64) {
      const step = def.steps[run.step]!
      if (run.have < stepNeed(step)) break
      run.step++
      run.have = 0
      completed++
      bus.emit('errand:stepDone', { id: run.id, step: run.step - 1, next: run.step })
      if (run.step >= def.steps.length) {
        this.finish(run, def)
        return completed
      }
      // Entering the next step: does the kid already have what it wants?
      this.autoSatisfy(run, def)
    }
    return completed
  }

  /**
   * A NEWLY ENTERED STEP IS CHECKED AGAINST WHAT THE KID ALREADY HAS.
   *
   * A three-year-old who wandered into the garden and picked three apples before
   * ever meeting the zookeeper must not be told to go and pick three apples.
   * `gather` reads the bag; `catch` reads the book. `talk`, `make`, `deliver`
   * and `build` are ACTS — they have to actually happen — so they are left
   * alone.
   */
  private autoSatisfy(run: ErrandRun, def: ErrandDef): void {
    let guard = 0
    while (run.step < def.steps.length && guard++ < 64) {
      const step = def.steps[run.step]!
      let have = 0
      if (step.kind === 'gather') have = this.reader.countItem(step.item)
      else if (step.kind === 'catch') have = this.reader.countFound(stepSubject(step))
      else break
      if (have < stepNeed(step)) {
        run.have = Math.min(have, stepNeed(step) - 1)
        break
      }
      run.have = have
      run.step++
      run.have = 0
      bus.emit('errand:stepDone', { id: run.id, step: run.step - 1, next: run.step })
      if (run.step >= def.steps.length) {
        this.finish(run, def)
        return
      }
    }
  }

  private finish(run: ErrandRun, def: ErrandDef): void {
    this.runs.delete(run.id)
    this.done.set(run.id, clock.day)
    bus.emit('errand:finished', {
      id: run.id,
      giver: def.giver,
      ...(def.reward === undefined ? {} : { reward: def.reward }),
    })
    if (def.chain !== undefined && DEFS.has(def.chain)) {
      bus.emit('errand:chained', { from: run.id, to: def.chain })
    }
  }

  /**
   * QA / harness only: jump an errand to finished, paying the reward. There is
   * no path to this from anything a kid can tap.
   */
  forceComplete(id: string): boolean {
    const run = this.runs.get(id) ?? this.grant(id, { silent: true })
    const def = DEFS.get(id)
    if (run === null || run === undefined || def === undefined) return false
    run.step = def.steps.length
    run.have = 0
    this.finish(run, def)
    return true
  }

  // -- persistence ---------------------------------------------------------

  snapshot(): ErrandsSaveState {
    return {
      runs: this.active()
        .map((r) => ({ id: r.id, step: r.step, have: r.have, hidden: r.hidden, grantedDay: r.grantedDay }))
        .sort((a, b) => (a.id < b.id ? -1 : 1)),
      done: [...this.done.entries()]
        .map(([id, day]) => ({ id, day }))
        .sort((a, b) => (a.id < b.id ? -1 : 1)),
    }
  }

  /**
   * Never throws. Anything unrecognised is dropped, and a run whose step index
   * has drifted past its definition is clamped rather than lost — a kid whose
   * errand data changed under them lands on the LAST step, not on a broken card.
   */
  restore(state: ErrandsSaveState): void {
    this.runs.clear()
    this.done.clear()
    for (const d of state.done ?? []) {
      if (typeof d?.id !== 'string' || d.id === '') continue
      const day = Math.round(Number(d.day))
      this.done.set(d.id, Number.isFinite(day) ? Math.max(0, day) : 0)
    }
    for (const r of state.runs ?? []) {
      if (typeof r?.id !== 'string' || r.id === '') continue
      const def = DEFS.get(r.id)
      const maxStep = def === undefined ? 0 : def.steps.length
      const step = Math.round(Number(r.step))
      const have = Math.round(Number(r.have))
      this.runs.set(r.id, {
        id: r.id,
        step: Number.isFinite(step) ? Math.max(0, Math.min(maxStep, step)) : 0,
        have: Number.isFinite(have) ? Math.max(0, have) : 0,
        hidden: r.hidden === true,
        grantedDay: Math.max(1, Math.round(Number(r.grantedDay)) || 1),
      })
    }
  }

  resetForTest(): void {
    this.runs.clear()
    this.done.clear()
  }
}

/** Does an observation satisfy this step? Pure — exported for tests. */
export function matches(step: ErrandStep, o: Observation): boolean {
  if (step.kind !== o.kind) return false
  switch (step.kind) {
    case 'talk':
      return o.what === step.npc
    case 'gather':
      return o.what === step.item
    case 'make':
      return o.what === step.recipe
    case 'deliver':
      return o.what === step.item && o.to === step.to
    case 'build': {
      if (step.sceneId !== undefined && o.sceneId !== step.sceneId) return false
      if (step.defId !== undefined) return o.what === step.defId
      if (step.category !== undefined) return o.category === step.category
      return false
    }
    case 'catch': {
      if (step.species !== undefined) return o.what === step.species
      if (step.group !== undefined) return o.group === step.group
      return false
    }
  }
}

export const errands = new Errands()

// ---------------------------------------------------------------------------
// wiring
// ---------------------------------------------------------------------------

let installed = false

/**
 * Hooks the engine onto the bus. Called once from `main.ts`, AFTER the content
 * registration pass so `chain` targets resolve. Idempotent.
 *
 * `reader` is injected by main.ts (bag + book) so this file imports neither.
 */
export function installErrands(reader: ErrandWorldReader): void {
  errands.setWorldReader(reader)
  if (installed) return
  installed = true

  bus.on('errand:observe', (e) => {
    errands.observe(e)
  })

  // FREE ADAPTERS — the build, inventory and collection crews report nothing.
  bus.on('build:placed', (e) => {
    errands.observe({
      kind: 'build',
      what: String(e.placement.defId),
      count: 1,
      sceneId: String(e.sceneId),
      category: categoryHint(String(e.placement.defId)),
    })
  })
  bus.on('inventory:gained', (e) => {
    errands.observe({ kind: 'gather', what: e.itemId, count: e.count })
  })
  bus.on('collection:found', (e) => {
    errands.observe({ kind: 'catch', what: e.id, count: 1, group: e.kind })
  })

  // A chained errand is granted the moment its parent lands.
  bus.on('errand:chained', (e) => {
    errands.grant(e.to)
  })
}

/**
 * The category of a placeable, without importing the placeable registry into
 * core/errands (which would make this file un-unit-testable). `installErrands`
 * callers may override it; the default answers "unknown", which only means a
 * `category` build step needs an explicit `errand:observe`.
 */
let categoryHint: (defId: string) => string = () => ''
export function setCategoryHint(fn: (defId: string) => string): void {
  categoryHint = fn
}
